import {readApiModel} from '../../../helpers/apiHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiCompany, CompanyModel} from '../../../models/api/companyModel';
import {putAsync} from '../../../services/http/http';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {TeamsActionsEnum, TeamUpdatedAction} from '../teams/teamsActions';
import {getFlatTeams} from '../teams/teamsSelectors';
import {getCompany} from './companySelectors';

/*
 * Action types.
 */

export enum CompanyActionsEnum {
  COMPANY_UPDATED = '@@data/company/COMPANY_UPDATED',
}

/*
 * Types.
 */

export interface CompanyUpdatedAction extends ActionWithPayload<CompanyModel> {
  type: CompanyActionsEnum.COMPANY_UPDATED;
}

/*
 * Action creators.
 */

export interface SaveCompanyFormActionOptions {
  name: string;
  companySize: number;
}

/**
 * Save company complete onboarding params locally to maintain form state when
 * going back and forth in the onboarding carousel. When we complete the whole
 * form, we make a PUT request with the local form values to complete company
 * onboarding.
 */
export function saveCompanyFormAction(options: SaveCompanyFormActionOptions): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    // Update the company with the saved name and size.
    const companyUpdateAction: CompanyUpdatedAction = {
      type: CompanyActionsEnum.COMPANY_UPDATED,
      payload: {
        ...company,
        name: options.name,
        preferences: {
          ...company.preferences,
          companySize: options.companySize || 1,
        },
      },
    };
    dispatch(companyUpdateAction);
  };
}

export function completeCompanyOnboardingAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const teams = getFlatTeams(state);

    // These values should have been saved previously by the saveCompanyFormAction.
    const body = {
      company_name: company.name,
      company_size: company.preferences.companySize,
    };

    const result = await putAsync(`${company.url}/complete_company_onboarding`, body);

    dispatch(updateCompany(readApiModel(ApiCompany, result)));

    // If we only have one team, the endpoint will update its name as well. We do the same.
    if (teams.length !== 1) {
      return;
    }

    const firstTeamUpdateAction: TeamUpdatedAction = {
      type: TeamsActionsEnum.TEAM_UPDATED,
      payload: {
        ...teams[0],
        name: result.name,
      },
    };
    dispatch(firstTeamUpdateAction);
  };
}

export function updateCompany(newCompany: CompanyModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing company.
    const existingCompany = getState().data.company;

    // No need to do anything if the new company is older.
    if (existingCompany.updatedAt > newCompany.updatedAt) {
      return null;
    }

    const action: CompanyUpdatedAction = {
      type: CompanyActionsEnum.COMPANY_UPDATED,
      payload: {...existingCompany, ...newCompany},
    };
    return dispatch(action);
  };
}

/*
 * Union type.
 */

export type CompanyAction = CompanyUpdatedAction;
